/**
 * Global trade event bus — lightweight pub/sub for market-wide trade events,
 * order placements, and cancellations.
 *
 * Market streams push trade events here automatically. Consumers (volume
 * tracking, activity rail, sweep detection) subscribe without prop threading.
 */

export type TradeEventType = 'market_trade' | 'order_placed' | 'order_cancelled';

export interface MarketTradeEvent {
  type: TradeEventType;
  ticker: string;
  side: 'yes' | 'no';
  count: number;
  priceCents: number;
  timestamp: number;
  /** Order ID for placement/cancellation events */
  orderId?: string;
}

type TradeListener = (event: MarketTradeEvent) => void;

const listeners = new Set<TradeListener>();

export function emitTradeEvent(event: MarketTradeEvent): void {
  for (const fn of listeners) {
    try {
      fn(event);
    } catch {
      // don't let one bad listener break the bus
    }
  }
}

/** Convenience: emit a market trade (backward compat for existing callers) */
export function emitMarketTrade(
  ticker: string,
  side: 'yes' | 'no',
  count: number,
  priceCents: number
): void {
  emitTradeEvent({
    type: 'market_trade',
    ticker,
    side,
    count,
    priceCents,
    timestamp: Date.now(),
  });
}

/** Emit an order placement event */
export function emitOrderPlaced(
  ticker: string,
  side: 'yes' | 'no',
  count: number,
  priceCents: number,
  orderId?: string
): void {
  emitTradeEvent({
    type: 'order_placed',
    ticker,
    side,
    count,
    priceCents,
    timestamp: Date.now(),
    orderId,
  });
}

/** Emit an order cancellation event */
export function emitOrderCancelled(
  ticker: string,
  side: 'yes' | 'no',
  count: number,
  priceCents: number,
  orderId?: string
): void {
  emitTradeEvent({
    type: 'order_cancelled',
    ticker,
    side,
    count,
    priceCents,
    timestamp: Date.now(),
    orderId,
  });
}

export function onTradeEvent(listener: TradeListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
